import { create } from '@thoughtspot/logger';
import { ErrorCode } from '@thoughtspot/radiant-react/widgets/alert/alert-constants';
import { enableMuzeStudio, getOrgVizOverrideConfig, isAdvancedPivotTableEnabled, isCustomChartEnabled, isKPIChartEnabled, isVizOverrideConfigEnabled, } from '@thoughtspot/session-service';
import _ from 'lodash';
import { ErrorTypes, } from '../../core/chart-data-validation';
import { getColumnNames } from '../../core/chart-model-utils';
import { chartSpecificConstants, chartTypeInDisplayOrder, chartTypeInDisplayOrderWithKpiEnabled, chartTypeSwaps, getClassicExperienceChartTypes, getEquivalentChartType, HIDDEN_CHARTS, newExperienceChartTypes, tsHostedCustomCharts, } from '../../core/chart-type-specification-service';
import { ChartType } from '../../types';
const logger = create('chart-viz-util');
export var CHART_VIZ_UPDATE_TYPES;
(function (CHART_VIZ_UPDATE_TYPES) {
    CHART_VIZ_UPDATE_TYPES["RELOAD"] = "reload";
    CHART_VIZ_UPDATE_TYPES["REFETCH"] = "refetch";
    CHART_VIZ_UPDATE_TYPES["RENDER"] = "render";
    CHART_VIZ_UPDATE_TYPES["REFLOW"] = "reflow";
    CHART_VIZ_UPDATE_TYPES["UPDATE_CHART_ZOOM"] = "update-chart-zoom";
    CHART_VIZ_UPDATE_TYPES["CHART_ZOOM_OUT"] = "chart-zoom-out";
    CHART_VIZ_UPDATE_TYPES["CHART_REDRAW"] = "chart-redraw";
    CHART_VIZ_UPDATE_TYPES["UPDATE_PIVOT_SETTINGS"] = "update-pivot-settings";
    CHART_VIZ_UPDATE_TYPES["TOGGLE_PIVOT_SUMMARIES"] = "toggle-pivot-summaries";
})(CHART_VIZ_UPDATE_TYPES || (CHART_VIZ_UPDATE_TYPES = {}));
export const isChartSupported = (chartType) => {
    if (chartType === ChartType.Kpi) {
        if (isKPIChartEnabled()) {
            return true;
        }
        return false;
    }
    return true;
};
export const getErrorDataConstants = () => ({
    [ErrorTypes.UNIQUE_X_VALUES_ALL_DATA]: {
        message: 'dataDisabledChartExplanation.UNIQUE_X_VALUES_ALL_DATA',
        icon: '/resources/img/no_result_found_icon.svg',
    },
    [ErrorTypes.UNIQUE_X_VALUES_PER_SERIES]: {
        message: 'dataDisabledChartExplanation.UNIQUE_X_VALUES_PER_SERIES',
        icon: '/resources/img/no_result_found_icon.svg',
    },
    [ErrorTypes.NO_DATA]: {
        message: 'answer.noData.message',
        description: 'answer.noData.description',
        errorCode: ErrorCode.LOCAL_VISUALIZATION,
    },
    [ErrorTypes.CHART_NOT_AVAILABLE]: {
        message: 'dataDisabledChartExplanation.CHART_NO_LONGER_AVAILABLE',
        icon: '/resources/img/no_result_found_icon.svg',
    },
    [ErrorTypes.ONLY_ONE_Y_AXIS_COLUMN_SUPPORTED]: {
        message: 'dataDisabledChartExplanation.ONLY_ONE_Y_AXIS_COLUMN_SUPPORTED',
        icon: '/resources/img/no_result_found_icon.svg',
    },
    [ErrorTypes.NON_NEGATIVE_VALUES_NEEDED]: {
        message: 'dataDisabledChartExplanation.NON_NEGATIVE_VALUES_NEEDED',
        icon: '/resources/img/no_result_found_icon.svg',
    },
    [ErrorTypes.PIE_NO_POSITIVE_VALUES]: {
        message: 'dataDisabledChartExplanation.PIE_NO_POSITIVE_VALUES',
        icon: '/resources/img/no_result_found_icon.svg',
    },
    [ErrorTypes.VIZ_BLANK_FOR_ALL_NEGATIVE_VALUES]: {
        message: 'dataDisabledChartExplanation.VIZ_BLANK_FOR_ALL_NEGATIVE_VALUES',
        icon: '/resources/img/no_result_found_icon.svg',
    },
    [ErrorTypes.DATA_INCOMPLETE_PIVOT]: {
        message: 'dataDisabledChartExplanation.DATA_INCOMPLETE_PIVOT',
        icon: '/resources/img/no_result_found_icon.svg',
    },
    [ErrorTypes.TREEMAP_CARDINALITY_ERROR]: {
        message: 'dataDisabledChartExplanation.CARDINALITY',
        icon: '/resources/img/no_result_found_icon.svg',
        dynamicValues: {
            1: chartSpecificConstants.TREEMAP_CARDINALITY_THRESHOLD,
        },
    },
    [ErrorTypes.SANKEY_CARDINALITY_ERROR]: {
        message: 'dataDisabledChartExplanation.SANKEY_CARDINALITY',
        icon: '/resources/img/no_result_found_icon.svg',
        dynamicValues: {
            1: chartSpecificConstants.SANKEY_CHART_CARDINALITY_THRESHOLD,
        },
    },
    [ErrorTypes.SPIDER_WEB_CARDINALITY_ERROR]: {
        message: 'dataDisabledChartExplanation.LOW_CARDINALITY',
        icon: '/resources/img/no_result_found_icon.svg',
        dynamicValues: {
            1: chartSpecificConstants.SPIDER_LOW_CARDINALITY_THRESHOLD,
        },
    },
    [ErrorTypes.INVALID_DATA_TOOLTIP_CONFIG]: {
        message: 'dataDisabledChartExplanation.INVALID_DATA_TOOLTIP_CONFIG',
        icon: '/resources/img/no_result_found_icon.svg',
        errorCode: ErrorCode.LOCAL_VISUALIZATION,
        ctaText: 'charts.chartEditor.EDIT_TOOLTIP',
    },
    [ErrorTypes.ONE_MANY_MAPPING_TOOLTIP_CONFIG]: {
        message: 'dataDisabledChartExplanation.ONE_MANY_MAPPING_TOOLTIP_CONFIG',
        icon: '/resources/img/no_result_found_icon.svg',
        dynamicValues: {
            columns: '',
        },
        errorCode: ErrorCode.LOCAL_VISUALIZATION,
    },
    [ErrorTypes.LEGEND_CARDINALITY_ERROR]: {
        message: 'chartCardinality.fullCardinalityError',
        icon: '/resources/img/no_result_found_icon.svg',
    },
    [ErrorTypes.FAILED_TO_RENDER_VIZ]: {
        message: 'alert.errorCause.UNKNOWN',
        icon: '/resources/img/generic-warning.svg',
    },
    [ErrorTypes.MISSING_GEO_CONFIG]: {
        message: 'answer.mapLoadFail.message',
        description: 'answer.mapLoadFail.missingGeoConfig.description',
        errorCode: ErrorCode.LOCAL_GENERAL_WARNING,
    },
    [ErrorTypes.MISSING_GEO_DATA]: {
        message: 'answer.mapLoadFail.message',
        description: 'answer.mapLoadFail.missingGeoData.description',
        errorCode: ErrorCode.LOCAL_GENERAL_WARNING,
    },
    [ErrorTypes.CHART_TYPE_DISABLED_BY_ADMIN]: {
        message: 'dataDisabledChartExplanation.DISABLED_BY_ADMIN',
        icon: '/resources/img/no_result_found_icon.svg',
    },
    [ErrorTypes.DATA_POINTS_LIMIT_EXCEEDED]: {
        message: 'dataDisabledChartExplanation.DATA_POINTS_LIMIT_EXCEEDED',
        icon: '/resources/img/no_result_found_icon.svg',
    },
});
export var CHART_REDRAW_ACTION_TYPES;
(function (CHART_REDRAW_ACTION_TYPES) {
    CHART_REDRAW_ACTION_TYPES["DATA_LABEL"] = "data-label";
    CHART_REDRAW_ACTION_TYPES["DATA_MARKERS"] = "data-marker";
    CHART_REDRAW_ACTION_TYPES["ALL_LABELS"] = "all-labels";
    CHART_REDRAW_ACTION_TYPES["STACK_LABEL"] = "stack-label";
    CHART_REDRAW_ACTION_TYPES["X_GRIDLINE"] = "x-gridline";
    CHART_REDRAW_ACTION_TYPES["Y_GRIDLINE"] = "y-gridline";
    CHART_REDRAW_ACTION_TYPES["OVERLAY_HEATMAP"] = "overlay-heatmap";
    CHART_REDRAW_ACTION_TYPES["AXIS_RANGE"] = "y-axis-range";
})(CHART_REDRAW_ACTION_TYPES || (CHART_REDRAW_ACTION_TYPES = {}));
export var AnswerMode;
(function (AnswerMode) {
    AnswerMode["ANSWER"] = "ANSWER";
    AnswerMode["PINBOARD_TILE"] = "PINBOARD_TILE";
    AnswerMode["PRESENT"] = "PRESENT";
    AnswerMode["MONITOR"] = "MONITOR";
    AnswerMode["EXPLORE"] = "EXPLORE";
    AnswerMode["CONV_ASSIST_ANSWER_EDIT"] = "CONV_ASSIST_ANSWER_EDIT";
    AnswerMode["WATCHLIST"] = "WATCHLIST";
})(AnswerMode || (AnswerMode = {}));
const ChartVizActionMap = {
    [CHART_REDRAW_ACTION_TYPES.DATA_LABEL]: {
        bindAxisRedrawEvents: false,
        actionHandler: (chart, params) => {
            chart.toggleDataLabel(params.enabled, params.columnId);
        },
    },
    [CHART_REDRAW_ACTION_TYPES.STACK_LABEL]: {
        bindAxisRedrawEvents: true,
        actionHandler: (chart, params) => {
            chart.setStackLabelVisibility(params.enabled);
        },
    },
    [CHART_REDRAW_ACTION_TYPES.X_GRIDLINE]: {
        bindAxisRedrawEvents: true,
        actionHandler: (chart, params) => {
            chart.enableXAxisGridLines(params.enabled);
        },
    },
    [CHART_REDRAW_ACTION_TYPES.Y_GRIDLINE]: {
        bindAxisRedrawEvents: true,
        actionHandler: (chart, params) => {
            chart.enableYAxisGridLines(params.enabled);
        },
    },
    [CHART_REDRAW_ACTION_TYPES.DATA_MARKERS]: {
        bindAxisRedrawEvents: false,
        actionHandler: (chart, params) => {
            chart.setMarkerVisibility(params.enabled);
        },
    },
    [CHART_REDRAW_ACTION_TYPES.OVERLAY_HEATMAP]: {
        bindAxisRedrawEvents: false,
        actionHandler: (chart, params) => {
        },
    },
    [CHART_REDRAW_ACTION_TYPES.AXIS_RANGE]: {
        bindAxisRedrawEvents: true,
        actionHandler: (chart, params) => {
            chart.setAxisRangeForAxes(params.axisId, params.range);
        },
    },
    [CHART_REDRAW_ACTION_TYPES.ALL_LABELS]: {
        bindAxisRedrawEvents: false,
        actionHandler: (chart, params) => {
            chart.toggleAllDataLabels(params.enabled);
        },
    },
};
const getDynamicValuesFor1ManyMapping = (chartDataRenderError, chartViz, t) => {
    var _a;
    const oneToManyMappingColumns = ((_a = chartDataRenderError === null || chartDataRenderError === void 0 ? void 0 : chartDataRenderError.info) === null || _a === void 0 ? void 0 : _a.oneToManyMappingColumns) || [];
    const columnNames = getColumnNames(chartViz, oneToManyMappingColumns) || [];
    let columns = '';
    if (columnNames.length === 1) {
        columns = columnNames[0];
    }
    else {
        columns = columnNames.slice(0, columnNames.length - 1).join(', ');
    }
    if (columnNames.length >= 2) {
        columns += ` ${t('and')} ${columnNames[columnNames.length - 1]}`;
    }
    return {
        columns,
        prefix: (oneToManyMappingColumns === null || oneToManyMappingColumns === void 0 ? void 0 : oneToManyMappingColumns.length) === 1
            ? t('oneToManyMappingDataErrorPrefix1')
            : t('oneToManyMappingDataErrorPrefix2'),
    };
};
export const getChartDataValidationErrMessage = (chartDataRenderError, envOptions, t, chartViz) => {
    const error = getErrorDataConstants()[chartDataRenderError.type];
    let dynamicValues = {};
    switch (chartDataRenderError.type) {
        case ErrorTypes.ONE_MANY_MAPPING_TOOLTIP_CONFIG:
            dynamicValues = getDynamicValuesFor1ManyMapping(chartDataRenderError, chartViz, t);
            break;
        case ErrorTypes.LEGEND_CARDINALITY_ERROR:
            dynamicValues = {
                1: envOptions.flags.maxChartLegendCardinality,
            };
            break;
        default:
            dynamicValues = error.dynamicValues || {};
    }
    return t(error.message, dynamicValues);
};
const commonChartRedrawHandler = (chart, actionParams) => {
    const actionConfig = ChartVizActionMap[actionParams.actionType];
    actionConfig.actionHandler(chart, actionParams.params);
    chart.redraw();
};
export const updateChartEvent = (chartRef, actionType, actionParams) => {
    const chart = chartRef.current;
    if (_.isNil(chart)) {
        logger.error('chart is not initialized and is being accessed');
        return;
    }
    switch (actionType) {
        case CHART_VIZ_UPDATE_TYPES.RENDER:
        case CHART_VIZ_UPDATE_TYPES.REFETCH:
        case CHART_VIZ_UPDATE_TYPES.UPDATE_PIVOT_SETTINGS:
            requestAnimationFrame(() => {
                chart.render();
            });
            break;
        case CHART_VIZ_UPDATE_TYPES.UPDATE_CHART_ZOOM:
            chart.switchChartZoomMode();
            break;
        case CHART_VIZ_UPDATE_TYPES.CHART_ZOOM_OUT:
            chart.chartZoomOut();
            break;
        case CHART_VIZ_UPDATE_TYPES.TOGGLE_PIVOT_SUMMARIES:
            chart.togglePivotSummaries(actionParams.chartModel);
            break;
        case CHART_VIZ_UPDATE_TYPES.CHART_REDRAW:
            if (!actionParams) {
                logger.error('Params not passed for Chart Redraw');
                return;
            }
            commonChartRedrawHandler(chart, actionParams);
            break;
        default:
    }
};
export const updateChartHandler = (chart, actionType, actionParams) => {
    if (chart) {
        updateChartEvent(chart, actionType, actionParams);
    }
};
export const getHiddenChartsFromVizOverrideConfig = () => {
    var _a;
    const hiddenChartsFromVizOverrideConfig = new Set();
    if (isVizOverrideConfigEnabled()) {
        const orgVizOverrideConfig = getOrgVizOverrideConfig();
        const allowedChartTypesMap = (_a = orgVizOverrideConfig === null || orgVizOverrideConfig === void 0 ? void 0 : orgVizOverrideConfig.chartOverrideConfig) === null || _a === void 0 ? void 0 : _a.allowedChartTypesMap;
        if (allowedChartTypesMap) {
            Object.entries(allowedChartTypesMap).forEach(([chartType, isChartTypeAllowed]) => {
                if (!isChartTypeAllowed) {
                    hiddenChartsFromVizOverrideConfig.add(chartType);
                    if (chartType === ChartType.PivotTable) {
                        hiddenChartsFromVizOverrideConfig.add(ChartType.AdvancedPivotTable);
                    }
                }
            });
        }
    }
    return hiddenChartsFromVizOverrideConfig;
};
export const getHiddenChartsBasedOnSessionServiceFlags = () => {
    const hiddenChartsBasedOnSessionServiceFlags = new Set();
    const CHART_TYPE_ENABLER = {
        [ChartType.Kpi]: isKPIChartEnabled,
        [ChartType.AdvancedPivotTable]: isAdvancedPivotTableEnabled,
    };
    Object.entries(CHART_TYPE_ENABLER).forEach(([chartType, isEnabled]) => {
        if (!isEnabled()) {
            hiddenChartsBasedOnSessionServiceFlags.add(chartType);
        }
    });
    return hiddenChartsBasedOnSessionServiceFlags;
};
export const swapChartTypes = (chartTypes, chartTypeSwaps) => {
    const updatedChartTypes = [...chartTypes];
    chartTypeSwaps.forEach(([firstChartType, secondChartType]) => {
        const index1 = chartTypes.indexOf(firstChartType);
        const index2 = chartTypes.indexOf(secondChartType);
        if (index1 > -1 && index2 > -1) {
            [updatedChartTypes[index1], updatedChartTypes[index2]] = [
                updatedChartTypes[index2],
                updatedChartTypes[index1],
            ];
        }
    });
    return updatedChartTypes;
};
export const getAvailableChartTypes = () => {
    let baseChartTypesInOrder = isKPIChartEnabled()
        ? chartTypeInDisplayOrderWithKpiEnabled
        : chartTypeInDisplayOrder;
    baseChartTypesInOrder = [...baseChartTypesInOrder, ...tsHostedCustomCharts];
    const chartsToHide = new Set([
        ...HIDDEN_CHARTS,
        ...getHiddenChartsFromVizOverrideConfig(),
        ...getHiddenChartsBasedOnSessionServiceFlags(),
    ]);
    baseChartTypesInOrder = baseChartTypesInOrder.filter(chartType => !chartsToHide.has(chartType));
    baseChartTypesInOrder = swapChartTypes(baseChartTypesInOrder, chartTypeSwaps);
    return baseChartTypesInOrder;
};
export const getAvailableChartTypeForChartSelector = () => {
    const availableChartTypes = getAvailableChartTypes();
    const shouldHideMuzeStudio = () => {
        return !(enableMuzeStudio() || false);
    };
    let availableChartTypesForChartSelector = availableChartTypes.map(chartType => {
        if (chartType === ChartType.MuzeStudio && shouldHideMuzeStudio()) {
            return null;
        }
        if (newExperienceChartTypes.includes(chartType)) {
            return null;
        }
        return chartType;
    });
    availableChartTypesForChartSelector = availableChartTypesForChartSelector.filter(chartType => chartType !== null);
    availableChartTypesForChartSelector = [
        ...new Set(availableChartTypesForChartSelector),
    ];
    return availableChartTypesForChartSelector;
};
export const getAvailableChartTypeForChartSelectorWithSwap = () => {
    const chartTypes = getAvailableChartTypeForChartSelector();
    const enabledClassicTypes = getClassicExperienceChartTypes();
    return chartTypes.map(chartType => {
        if (enabledClassicTypes.includes(chartType)) {
            return getEquivalentChartType(chartType);
        }
        return chartType;
    });
};
export const validateChartConfiguration = (config) => {
    const error = { hasError: false };
    if (config.chartType === ChartType.CustomChart) {
        if (!isCustomChartEnabled()) {
            return {
                hasError: true,
                errorMessage: ErrorTypes.CHART_TYPE_DISABLED_BY_ADMIN,
            };
        }
        return error;
    }
    if (!isChartSupported(config.chartType)) {
        error.hasError = true;
        error.errorMessage = ErrorTypes.CHART_NOT_AVAILABLE;
    }
    else if (!getAvailableChartTypes().includes(config.chartType)) {
        error.hasError = true;
        error.errorMessage = ErrorTypes.CHART_TYPE_DISABLED_BY_ADMIN;
    }
    return error;
};
export const DEBOUNCE_WAIT_MS = 200;
export const KPI_ANOMALY_REFLOW_DELAY_MS = 600;
export const testHooks = {
    getDynamicValuesFor1ManyMapping,
};
//# sourceMappingURL=chart-viz.util.js.map